import { AlertTriangle } from "lucide-react";

/**
 * Last-line UI for when the React tree itself crashes.
 *
 * Sentry's ErrorBoundary renders this when a render-time exception
 * escapes a child component. We've already captured the error to
 * Sentry by the time this shows; this is purely for the user.
 *
 * Kept deliberately small: no API calls, no router dependencies, no
 * data access. Anything that itself could fail would loop us back
 * here, so this component must be bulletproof.
 */
export function ErrorFallback({ resetError }: { resetError?: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-6">
      <div className="max-w-md w-full rounded-xl border border-line bg-surface p-8 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-red-50 dark:bg-red-500/10 p-2">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-base font-bold text-ink">
              Something went wrong
            </h1>
            <p className="mt-1 text-sm text-ink-3">
              The page hit an unexpected error and couldn't render. We've
              been notified and will take a look. In the meantime, try
              reloading — it usually clears up.
            </p>
            <div className="mt-5 flex items-center gap-2">
              <button
                onClick={() => {
                  resetError?.();
                  window.location.reload();
                }}
                className="rounded-md bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700"
              >
                Reload page
              </button>
              <button
                onClick={() => {
                  window.location.href = "/";
                }}
                className="rounded-md border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised"
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
